/* eslint complexity: ["error", { max: 9 }] -- applies to entire file */
import { getDocumentAsync } from "expo-document-picker";
import {
  getCameraPermissionsAsync,
  getMediaLibraryPermissionsAsync,
  getPendingResultAsync,
  launchCameraAsync,
  launchImageLibraryAsync,
  PermissionStatus,
  requestCameraPermissionsAsync,
  requestMediaLibraryPermissionsAsync,
} from "expo-image-picker";
import isNil from "lodash/isNil";

import type { DocumentMimeType } from "@/constants/Documents";
import { isAndroid } from "@/utils/platform.util";
import { permissionDeniedAlert } from "@/utils/shared.utils";

interface AssetPickerOptions {
  onError?: (error: Error) => void;
}

interface DocumentPickerOptions extends AssetPickerOptions {
  documentMimeTypes?: DocumentMimeType[];
}

interface RequestAssetPickerPermissionParams {
  checkPermission: () => Promise<{ status: PermissionStatus }>;
  permissionName: string;
  requestPermission: () => Promise<{ status: PermissionStatus }>;
}

const nonCanceledAsset = (
  result: Awaited<ReturnType<typeof launchCameraAsync>>,
) => (result.canceled ? null : result.assets[0]);

const requestAssetPickerPermission = async ({
  checkPermission,
  permissionName,
  requestPermission,
}: RequestAssetPickerPermissionParams) => {
  const { status: initialStatus } = await checkPermission();

  if (initialStatus === PermissionStatus.DENIED) {
    permissionDeniedAlert(permissionName);
    return false;
  }

  if (initialStatus === PermissionStatus.GRANTED) {
    return true;
  }

  const { status } = await requestPermission();
  return status === PermissionStatus.GRANTED;
};

const pickDocument = async ({
  documentMimeTypes,
  onError,
}: DocumentPickerOptions) => {
  try {
    const result = await getDocumentAsync({
      type: documentMimeTypes || "*/*",
    });

    return result.canceled ? null : result.assets[0];
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    onError?.(error as Error);
    return null;
  }
};

const useAssetsPicker = ({ quality = 1 }: { quality?: number } = {}) => {
  const pickImageFromGallery = async ({ onError }: AssetPickerOptions) => {
    const hasPermission = await requestAssetPickerPermission({
      checkPermission: getMediaLibraryPermissionsAsync,
      permissionName: "Photo gallery",
      requestPermission: requestMediaLibraryPermissionsAsync,
    });

    if (!hasPermission) return null;

    try {
      const result = await launchImageLibraryAsync({
        aspect: [4, 3],
        mediaTypes: "images",
        quality,
      });

      return result.canceled ? null : result.assets[0];
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      onError?.(error as Error);
      return null;
    }
  };

  const takePhoto = async ({ onError }: AssetPickerOptions) => {
    const hasPermission = await requestAssetPickerPermission({
      checkPermission: getCameraPermissionsAsync,
      permissionName: "Camera",
      requestPermission: requestCameraPermissionsAsync,
    });

    if (!hasPermission) return null;

    try {
      const result = await launchCameraAsync({
        aspect: [4, 3],
        quality,
      });

      if (!isAndroid()) {
        return nonCanceledAsset(result);
      }

      // See: https://docs.expo.dev/versions/latest/sdk/imagepicker/#imagepickergetpendingresultasync
      // Android system sometimes kills the MainActivity after the ImagePicker finishes.
      // When this happens, we lose the data selected using the ImagePicker.
      // However, you can retrieve the lost data by calling getPendingResultAsync
      const pendingResults = await getPendingResultAsync();
      // if an array is returned, it's only ever one result at most in the array
      const pendingResult = Array.isArray(pendingResults)
        ? pendingResults[0]
        : pendingResults;

      if (isNil(pendingResult)) {
        // no data was lost, return original result
        return nonCanceledAsset(result);
      }

      if ("message" in pendingResult) {
        throw new Error(
          `takePhoto (on android) encountered an error, with code '${pendingResult.code}', exception '${pendingResult.exception}', and message '${pendingResult.message}'`,
        );
      }
      return nonCanceledAsset(pendingResult);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      onError?.(error as Error);
      return null;
    }
  };

  return { pickDocument, pickImageFromGallery, takePhoto };
};

export default useAssetsPicker;
